import { useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { useVault } from '../lib/vault';

export function LoginScreen() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const afterLogin = useVault((s) => s.afterLogin);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setInfo(null);
    try {
      const res = await api.login({ username, password, totp, rememberDevice });
      if (res.status === 'ok') {
        afterLogin({
          accessToken: res.accessToken,
          user: {
            id: res.user.id,
            username: res.user.username,
            email: res.user.email,
            role: res.user.role,
          },
          encryptedPrivateKey: res.encryptedPrivateKey,
          vaultState: res.vaultState,
        });
      } else if (res.status === 'ip_challenge') {
        setInfo(res.message);
      } else {
        setError(res.message);
      }
    } catch (err: any) {
      setError(err?.body?.message ?? 'Sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="card w-full max-w-sm p-6">
        <div className="flex justify-center mb-6">
          <img src="/dragon-logo.png" alt="Dragon" className="h-7 w-auto" />
          <h1 className="sr-only">Dragon HQ</h1>
        </div>
        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <label className="text-xs text-ink-400 uppercase">Username</label>
            <input
              className="input mt-1"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
              autoComplete="username"
              required
            />
          </div>
          <div>
            <label className="text-xs text-ink-400 uppercase">Password</label>
            <input
              type="password"
              className="input mt-1"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>
          <div>
            <label className="text-xs text-ink-400 uppercase">Authenticator Code</label>
            <input
              className="input mt-1 tracking-widest text-center"
              value={totp}
              onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              autoComplete="one-time-code"
              placeholder="──────"
            />
            <p className="mt-1 text-[11px] text-ink-500">
              Leave blank if you previously trusted this device.
            </p>
          </div>
          <label className="flex items-center gap-2 text-xs text-ink-300 select-none cursor-pointer">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-ink-700 bg-ink-900 text-dragon-500 focus:ring-dragon-500"
              checked={rememberDevice}
              onChange={(e) => setRememberDevice(e.target.checked)}
            />
            Trust this device for 7 days (skip code on next sign-in)
          </label>
          {error && (
            <div className="rounded border border-red-900 bg-red-950/40 p-2 text-sm text-red-300">
              {error}
            </div>
          )}
          {info && (
            <div className="rounded border border-blue-900 bg-blue-950/40 p-2 text-sm text-blue-200">
              {info}
            </div>
          )}
          <button type="submit" className="btn-primary w-full" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        <p className="mt-4 text-xs text-ink-500">
          Accounts are provisioned by your administrator via the server CLI.
        </p>
      </div>
    </div>
  );
}
